import { DateTime } from 'luxon';
import * as i0 from '@angular/core';
import { Pipe, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

const SE_FE_DATE_TIME_FORMAT_PARTS = {
    day: { day: 'numeric' },
    weekday: { weekday: 'short' },
    month: { month: 'short' },
    year: { year: 'numeric' },
    time: { hour: 'numeric', minute: 'numeric', timeZoneName: 'short' },
};
const SE_FE_DATE_TIME_FORMATS = {
    MONTH: {
        precision: 'month',
        format: ['month', 'non-current-year'],
    },
    SHORT_DATE: {
        precision: 'day',
        format: ['month', 'day', 'non-current-year'],
    },
    SHORT_DATE_TIME: {
        precision: 'minute',
        format: ['month', 'day', 'non-current-year', 'time'],
    },
    DATE: {
        precision: 'day',
        format: ['month', 'day', 'year'],
    },
    DATE_TIME: {
        precision: 'minute',
        format: ['month', 'day', 'year', 'time'],
    },
    FULL_DATE: {
        precision: 'day',
        format: ['weekday', 'month', 'day', 'year'],
    },
    FULL_DATE_TIME: {
        precision: 'minute',
        format: ['weekday', 'month', 'day', 'year', 'time'],
    },
    TIME: {
        precision: 'minute',
        format: ['time'],
    },
    YEAR: {
        precision: 'year',
        format: ['year'],
    }
};

const ONE_MINUTE = 60000;
const DEVICE_ZONE = DateTime.local().zoneName;
const TIME_RX = /\d{1,2}:\d{2}/; // if we see 0:0 we assume it's a time
const ZEROS_RX = /:00/g;
class SeFeDateTime {
    constructor(startDate, endDateOrOptions, options) {
        if (!options) {
            options = typeof endDateOrOptions === 'object' ? endDateOrOptions : {};
            endDateOrOptions = 'INVALID';
        }
        const dateOpts = { zone: options.zone || 'local' };
        const date = DateTime.fromISO(startDate, dateOpts);
        const endDate = DateTime.fromISO(endDateOrOptions, dateOpts);
        const validDates = date.isValid && endDate.isValid;
        const validEndDate = !date.isValid && endDate.isValid;
        this.setFormatFlags();
        this.current = DateTime.now();
        this.isTime = TIME_RX.test(startDate);
        this.isRange = validDates && (+endDate - (+date)) >= ONE_MINUTE;
        this.startDate = validEndDate ? endDate : date; // fall back to a valid end date if date is invalid
        if (this.isRange)
            this.endDate = endDate;
    }
    // PUBLIC FORMAT GETTERS
    get date() { return this.preset('DATE'); }
    get dateTime() { return this.preset('DATE_TIME'); }
    get fullDate() { return this.preset('FULL_DATE'); }
    get fullDateTime() { return this.preset('FULL_DATE_TIME'); }
    get month() { return this.preset('MONTH'); }
    get shortDate() { return this.preset('SHORT_DATE'); }
    get shortDateTime() { return this.preset('SHORT_DATE_TIME'); }
    get time() { return this.preset('TIME'); }
    get year() { return this.preset('YEAR'); }
    preset(format) {
        if (!this.startDate.isValid)
            return '';
        const range = this.renderAsRange(format);
        const formats = this.buildFormats(format, range);
        return this.render(...formats);
    }
    buildFormats(formatName, range) {
        const presetParts = SE_FE_DATE_TIME_FORMATS[formatName].format;
        const includeYear = presetParts.includes('non-current-year') && !this.currentYear;
        const localZone = this.startDate.zoneName === this.deviceZone;
        const format = {};
        for (const part of presetParts)
            Object.assign(format, SE_FE_DATE_TIME_FORMAT_PARTS[part] || {});
        if (includeYear)
            Object.assign(format, SE_FE_DATE_TIME_FORMAT_PARTS.year);
        if (localZone)
            delete format.timeZoneName;
        // Return early if we are not rendering a range
        if (!range)
            return [format];
        // Adjust range formats for compressed ranges with logic
        const { hour, minute, timeZoneName } = format;
        const formatHasTime = 'hour' in format;
        const formatHasYear = 'year' in format;
        const formatHasMonth = 'month' in format;
        const formatHasDay = 'weekday' in format;
        const formatHasDate = 'day' in format;
        const formatHasZone = 'timeZoneName' in format;
        const compressTimeRange = SeFeDateTime.timeIsLast && formatHasTime && !this.differentDays;
        const compressDateRange = SeFeDateTime.dateIsLast && !formatHasTime && formatHasMonth && !formatHasDay && !this.differentMonths;
        const compressYearRange = SeFeDateTime.yearIsLast && !formatHasTime && formatHasMonth && formatHasYear && !this.differentYears;
        const start = Object.assign({}, format);
        let end = Object.assign({}, format);
        // DateTime Ranges
        if (formatHasTime) {
            delete start.timeZoneName;
            // Same Day -> Date, Time–Time
            if (compressTimeRange)
                end = { hour, minute, timeZoneName };
        }
        // Month Ranges
        else if (formatHasYear) {
            // Same year -> Month–Month, Year
            if (compressYearRange)
                delete start.year;
        }
        return [start, end, { compressTimeRange, compressDateRange }];
    }
    // This will remove some duplicate parts of a range, when the grammar of the locale-specific
    // format matches a pattern we recognize. Other grammars are not compressed.
    // so this -> Thu, Jan 1, 2022, 9:00 AM CST–Thu, Jan 1, 2022, 11:30 AM CST
    // becomes -> Thu, Jan 1, 2022, 9–11:30 AM CST
    // and this -> Jan 4, 2023–Jan 10, 2023
    // becomes -> Jan 4–10, 2023
    render(startFormat, endFormat, options = {}) {
        let start = startFormat ? this.startDate.toLocaleString(startFormat) : '';
        let end = endFormat ? this.endDate.toLocaleString(endFormat) : '';
        // Only show am/pm/zone once for shortened time ranges
        if (options.compressTimeRange) {
            start = this.removeAmPm(start);
        }
        // Only show one month for day ranges within the same month
        if (options.compressDateRange) {
            const month = new RegExp(`${this.endDate.toLocaleString(SE_FE_DATE_TIME_FORMAT_PARTS.month)}\\W? `);
            end = end.replace(month, '');
        }
        let compressed = start && end ? `${start}–${end}` : start;
        // Shorten times on the hour for am/pm locales (9:00 AM -> 9 AM)
        if (SeFeDateTime.has12HourTime) {
            compressed = compressed.replace(ZEROS_RX, '');
        }
        return compressed;
    }
    removeAmPm(start) {
        const endZone = ` ${this.endDate.toFormat('ZZZZ')}`;
        const endAmOrPm = this.endDate.toLocaleString(SE_FE_DATE_TIME_FORMAT_PARTS.time).replace(TIME_RX, '').replace(endZone, '');
        return start.replace(endAmOrPm, '');
    }
    renderAsRange(format) {
        const presetRange = this.isRange && typeof format === 'string' && SE_FE_DATE_TIME_FORMATS[format];
        if (!presetRange)
            return this.isRange;
        switch (presetRange.precision) {
            case 'day': return this.differentDays;
            case 'month': return this.differentMonths;
            case 'year': return this.differentYears;
            default: return this.isRange;
        }
    }
    setFormatFlags() {
        if (typeof SeFeDateTime.has12HourTime === 'boolean')
            return;
        const f = SE_FE_DATE_TIME_FORMAT_PARTS;
        const dt = DateTime.now().startOf('day').plus({ hours: 6 });
        const day = dt.toLocaleString(f.day);
        const year = dt.toLocaleString(f.year);
        const time = dt.toLocaleString(f.time);
        const monthDay = dt.toLocaleString(Object.assign(Object.assign({}, f.month), f.day));
        const monthYear = dt.toLocaleString(Object.assign(Object.assign({}, f.month), f.year));
        const dateTime = dt.toLocaleString(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, f.weekday), f.month), f.day), f.year), f.time));
        const h12 = dt.toLocaleString(DateTime.TIME_SIMPLE);
        const h24 = dt.toLocaleString(DateTime.TIME_24_SIMPLE);
        SeFeDateTime.timeIsLast = dateTime.endsWith(time);
        SeFeDateTime.yearIsLast = monthYear.endsWith(year);
        SeFeDateTime.dateIsLast = monthDay.endsWith(day);
        SeFeDateTime.has12HourTime = h12 !== h24;
    }
    // RANGE CHECKS
    get currentYear() {
        if (this.differentYears)
            return false;
        return this.startDate.year === this.current.year;
    }
    get differentYears() {
        return this.isRange && this.startDate.year !== this.endDate.year;
    }
    get differentMonths() {
        return this.isRange && this.startDate.month !== this.endDate.month && this.differentDays;
    }
    // NOTE: This has special logic to treat time ranges that start after noon
    // on one day and end before 5 AM on the following day as the same day.
    get differentDays() {
        if (!this.isRange)
            return false;
        const startDate = this.startDate;
        const endDate = this.endDate;
        const startSeconds = startDate.startOf('day').toSeconds();
        const endSeconds = endDate.startOf('day').toSeconds();
        const nextDaySeconds = startDate.startOf('day').plus({ days: 1 }).toSeconds();
        const sameDay = startSeconds === endSeconds;
        const nextDay = this.isTime && nextDaySeconds === endSeconds;
        const lateNightRange = nextDay && startDate.hour > 11 && endDate.hour < 5;
        return !sameDay && !lateNightRange;
    }
    get deviceZone() {
        return DEVICE_ZONE;
    }
}
SeFeDateTime.dateIsLast = undefined;
SeFeDateTime.has12HourTime = undefined;
SeFeDateTime.timeIsLast = undefined;
SeFeDateTime.yearIsLast = undefined;

// USAGE: Transform a tiimestamp, or an array of timestamps using a named
// pipe for each format, with an optional timezone as the second argument.
// {{ startDate | seFeDateTime }}
// {{ [startDate, endDate] | seFeShortDate:zone }}
// Use the proxy to test that the constructor is called correctly
const SeFeDateTimeProxy = {
    class: SeFeDateTime
};
function seFeDateTimeFormat(format) {
    return function (pointOrRange, zone) {
        const range = [].concat(pointOrRange);
        const ProxyClass = SeFeDateTimeProxy.class;
        if (range.length === 2) {
            return (new ProxyClass(range[0], range[1], { zone }))[format] || '';
        }
        else {
            return (new ProxyClass(range[0], { zone }))[format] || '';
        }
    };
}
class SeFeMonthPipe {
    constructor() {
        this.transform = seFeDateTimeFormat('month');
    }
}
SeFeMonthPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeMonthPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SeFeMonthPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeMonthPipe, name: "seFeMonth" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeMonthPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'seFeMonth' }]
        }] });
class SeFeShortDatePipe {
    constructor() {
        this.transform = seFeDateTimeFormat('shortDate');
    }
}
SeFeShortDatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeShortDatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SeFeShortDatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeShortDatePipe, name: "seFeShortDate" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeShortDatePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'seFeShortDate' }]
        }] });
class SeFeShortDateTimePipe {
    constructor() {
        this.transform = seFeDateTimeFormat('shortDateTime');
    }
}
SeFeShortDateTimePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeShortDateTimePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SeFeShortDateTimePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeShortDateTimePipe, name: "seFeShortDateTime" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeShortDateTimePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'seFeShortDateTime' }]
        }] });
class SeFeDatePipe {
    constructor() {
        this.transform = seFeDateTimeFormat('date');
    }
}
SeFeDatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SeFeDatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatePipe, name: "seFeDate" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'seFeDate' }]
        }] });
class SeFeDateTimePipe {
    constructor() {
        this.transform = seFeDateTimeFormat('dateTime');
    }
}
SeFeDateTimePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDateTimePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SeFeDateTimePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDateTimePipe, name: "seFeDateTime" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDateTimePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'seFeDateTime' }]
        }] });
class SeFeFullDatePipe {
    constructor() {
        this.transform = seFeDateTimeFormat('fullDate');
    }
}
SeFeFullDatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFullDatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SeFeFullDatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFullDatePipe, name: "seFeFullDate" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFullDatePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'seFeFullDate' }]
        }] });
class SeFeFullDateTimePipe {
    constructor() {
        this.transform = seFeDateTimeFormat('fullDateTime');
    }
}
SeFeFullDateTimePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFullDateTimePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SeFeFullDateTimePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFullDateTimePipe, name: "seFeFullDateTime" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFullDateTimePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'seFeFullDateTime' }]
        }] });
class SeFeYearPipe {
    constructor() {
        this.transform = seFeDateTimeFormat('year');
    }
}
SeFeYearPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeYearPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SeFeYearPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeYearPipe, name: "seFeYear" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeYearPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'seFeYear' }]
        }] });

class SeFeDateTimeModule {
}
SeFeDateTimeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDateTimeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeDateTimeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDateTimeModule, declarations: [SeFeMonthPipe,
        SeFeShortDatePipe,
        SeFeShortDateTimePipe,
        SeFeDatePipe,
        SeFeDateTimePipe,
        SeFeFullDatePipe,
        SeFeFullDateTimePipe,
        SeFeYearPipe], imports: [CommonModule], exports: [SeFeMonthPipe,
        SeFeShortDatePipe,
        SeFeShortDateTimePipe,
        SeFeDatePipe,
        SeFeDateTimePipe,
        SeFeFullDatePipe,
        SeFeFullDateTimePipe,
        SeFeYearPipe] });
SeFeDateTimeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDateTimeModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDateTimeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        SeFeMonthPipe,
                        SeFeShortDatePipe,
                        SeFeShortDateTimePipe,
                        SeFeDatePipe,
                        SeFeDateTimePipe,
                        SeFeFullDatePipe,
                        SeFeFullDateTimePipe,
                        SeFeYearPipe,
                    ],
                    exports: [
                        SeFeMonthPipe,
                        SeFeShortDatePipe,
                        SeFeShortDateTimePipe,
                        SeFeDatePipe,
                        SeFeDateTimePipe,
                        SeFeFullDatePipe,
                        SeFeFullDateTimePipe,
                        SeFeYearPipe,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SeFeDatePipe, SeFeDateTime, SeFeDateTimeModule, SeFeDateTimePipe, SeFeDateTimeProxy, SeFeFullDatePipe, SeFeFullDateTimePipe, SeFeMonthPipe, SeFeShortDatePipe, SeFeShortDateTimePipe, SeFeYearPipe, seFeDateTimeFormat };
